import {
  EEventContextAction,
  EEventType,
  ELegalDocumentKind,
} from '@mahawi/eshop-common/dist/src/types';
import { type Dispatch } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { getHead } from '../../head';
import { getCurrentVersion, getVersion } from '../../legal/legal-versions';
import {
  buildInterpolationValues,
  type ILegalResourceReader,
  readTermsContent,
  resolveTermsContent,
} from '../../legal/terms-content';
import TermsDocument from '../components/legal/terms-document';
import { eventSend } from '../reducers/event/reducer';
import { type ILegalState } from '../reducers/legal/types';
import { setActiveCategory } from '../reducers/products/reducer';

function TermsAndConditionsContainer({
  dispatch,
  Legal,
}: {
  dispatch: Dispatch;
  Legal: ILegalState;
}) {
  useEffect((): (() => void) => {
    dispatch(
      eventSend({
        type: EEventType.PAGE_TERMS_AND_CONDITION,
        action: EEventContextAction.ENTER,
      }),
    );
    dispatch(
      setActiveCategory({
        category: {
          uuid: '',
        },
      }),
    );

    return (): void => {
      dispatch(
        eventSend({
          type: EEventType.PAGE_TERMS_AND_CONDITION,
          action: EEventContextAction.LEAVE,
        }),
      );
    };
  }, [dispatch]);

  const { language = 'en', versionCode: requestedVersionCode } = useParams<{
    language: string;
    versionCode?: string;
  }>();

  const currentVersionCode: string =
    Legal.terms?.versionCode ??
    getCurrentVersion(ELegalDocumentKind.TERMS).versionCode;
  const displayedVersion = getVersion(
    ELegalDocumentKind.TERMS,
    requestedVersionCode ?? currentVersionCode,
  );
  const termsNamespace: string = displayedVersion?.namespace ?? 'customer';

  const { t, i18n, ready } = useTranslation([termsNamespace, 'customer'], {
    nsMode: 'fallback',
  });

  useEffect((): void => {
    const { title } = getHead(t, t('navigation.title.termsAndConditions'));
    document.title = title;
  }, [t]);

  if (!ready || !displayedVersion) {
    return <div className="container container--t terms" />;
  }

  const reader: ILegalResourceReader = i18n;
  const content = readTermsContent(
    reader,
    language.toLowerCase(),
    termsNamespace,
  );
  const isArchived: boolean =
    displayedVersion.versionCode !== currentVersionCode;

  return (
    <div className="container container--t terms">
      {isArchived ? (
        <p className="terms--archived" role="note">
          {t('legal.archivedVersionBanner')}{' '}
          <Link to={`/${language}/terms-and-conditions`}>
            {t('legal.showCurrentVersion')}
          </Link>
        </p>
      ) : null}

      {content ? (
        <TermsDocument
          content={resolveTermsContent(
            content,
            buildInterpolationValues(Legal.seller, {
              versionCode: displayedVersion.versionCode,
            }),
          )}
          language={language}
          versionCode={displayedVersion.versionCode}
          labels={{
            downloadOrderDocuments: t('legal.downloadOrderDocuments'),
            downloadWithdrawalForm: t('legal.downloadWithdrawalForm'),
            withdrawalLink: t('withdrawal.linkLabel'),
          }}
        />
      ) : null}

      <p className="terms--footer-note">
        Sounds from{' '}
        <a
          href="https://notificationsounds.com/"
          target="_blank"
          rel="nofollow noreferrer noopener"
          aria-label="Notification Sounds (opens in new tab)"
        >
          Notification Sounds
        </a>
        .
      </p>
    </div>
  );
}

const mapStateToProps = ({ Legal }: { Legal: ILegalState }) => ({
  Legal,
});

export default connect(mapStateToProps)(TermsAndConditionsContainer);
